import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import META_TRAVEL_ADS_LOCATION_CRITERION from "../../../constants/meta-travel-ads-criterion/metaTravelAdsLocationCriterion";
import { validatePOS } from "../../../Utils/validators/campaignSettingsValidators";

const TargetLocations = ({ selectedCountries, setSelectedCountries }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedContinents, setSelectedContinents] = useState([]);
  const [alertMessage, setAlertMessage] = useState("");

  useEffect(() => {
    const validateSelectedCountries = async () => {
      if (selectedCountries.length > 0) {
        const result = await validatePOS(selectedCountries.map(String));
        if (!result.success) {
          setAlertMessage("Some points of sale are invalid. Please review your selections.");
        } else {
          setAlertMessage("");
        }
      } else {
        setAlertMessage("");
      }
    };

    validateSelectedCountries();
  }, [selectedCountries]);

  const handleCountryChange = (countryCode) => {
    setSelectedCountries((prev) =>
      prev.includes(countryCode)
        ? prev.filter((code) => code !== countryCode)
        : [...prev, countryCode]
    );
  };

  const handleContinentChange = (continent, countries) => {
    const countryCodes = countries.map((country) => country.location_country_code);
    const allSelected = countryCodes.every((code) => selectedCountries.includes(code));

    if (allSelected) {
      setSelectedCountries((prev) => prev.filter((code) => !countryCodes.includes(code)));
      setSelectedContinents((prev) => prev.filter((c) => c !== continent));
    } else {
      setSelectedCountries((prev) => [
        ...prev,
        ...countryCodes.filter((code) => !prev.includes(code)),
      ]);
      setSelectedContinents((prev) =>
        prev.includes(continent) ? prev : [...prev, continent]
      );
    }
  };

  const handleSelectAll = () => {
    const allCodes = Object.values(META_TRAVEL_ADS_LOCATION_CRITERION)
      .flat()
      .map((country) => country.location_country_code);
    const allContinents = Object.keys(META_TRAVEL_ADS_LOCATION_CRITERION);

    if (selectedCountries.length === allCodes.length) {
      setSelectedCountries([]);
      setSelectedContinents([]);
      setIsSelectAll(false);
    } else {
      setSelectedCountries(allCodes);
      setSelectedContinents(allContinents);
      setIsSelectAll(true);
    }
  };

  const handleDeselectAll = () => {
    setSelectedCountries([]);
    setSelectedContinents([]);
    setIsSelectAll(false);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchQuery(value);
    setIsExpanded(!!value);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setIsExpanded(false);
  };

  const toggleExpandAllContinents = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box className="targetLocations">
      <Box
        display="flex"
        alignItems="center"
        className="sectionTitle selectActions"
        justifyContent="space-between"
      >
        <Box display="flex" alignItems="center">
          <Typography className="sectionTitlePos">
            Select Your Points of Sale
          </Typography>
          {alertMessage && (
            <Typography className="text-error-section">
              {alertMessage}
            </Typography>
          )}
        </Box>
        <Box display="flex" alignItems="center">
          <Box display="flex" alignItems="center">
            <SearchIcon fontSize="small" style={{ color: "gray", marginRight: "8px" }} />
            <TextField
              variant="standard"
              placeholder="Search Country"
              size="small"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearSearch}>
                      <ClearIcon fontSize="small" style={{ color: "gray" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              className="countrySearchField"
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                sx={{
                  paddingBlock: "2px",
                  color: "#BAB7B7",
                  "&.Mui-checked": { color: "#61D5A9" },
                  "& .MuiSvgIcon-root": { height: "14px", width: "14px" },
                }}
                checked={isSelectAll}
                onChange={handleSelectAll}
              />
            }
            label="Select All"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                sx={{
                  paddingBlock: "2px",
                  color: "#BAB7B7",
                  "&.Mui-checked": { color: "#61D5A9" },
                  "& .MuiSvgIcon-root": { height: "14px", width: "14px" },
                }}
                checked={
                  selectedCountries.length === 0 &&
                  selectedContinents.length === 0
                }
                onChange={handleDeselectAll}
              />
            }
            label="Deselect All"
          />
        </Box>
      </Box>

      <Box display="flex" alignItems="center" paddingLeft={3}>
        <Grid container spacing={2} wrap="nowrap">
          {Object.entries(META_TRAVEL_ADS_LOCATION_CRITERION).map(
            ([continent, countries]) => {
              // Sort countries so selected ones appear at the top, preserving alphabetical order within each group
              const sortedCountries = [...countries].sort((a, b) => {
                const aSelected = selectedCountries.includes(a.location_country_code);
                const bSelected = selectedCountries.includes(b.location_country_code);
                if (aSelected && !bSelected) return -1;
                if (!aSelected && bSelected) return 1;
                return a.location_canonical_name.localeCompare(b.location_canonical_name);
              });

              return (
                <Grid item xs={6} sm={6} md={6} key={continent}>
                  <Box className="countryGroup">
                    <FormControlLabel
                      className="continentLabel"
                      control={
                        <Checkbox
                          size="small"
                          sx={{
                            paddingBlock: "2px",
                            color: "#BAB7B7",
                            "&.Mui-checked": { color: "#61D5A9" },
                            "& .MuiSvgIcon-root": { height: "14px", width: "14px" },
                          }}
                          checked={selectedContinents.includes(continent)}
                          onChange={() => handleContinentChange(continent, countries)}
                        />
                      }
                      label={continent}
                    />
                    <FormGroup>
                      {(isExpanded ? sortedCountries : sortedCountries.slice(0, 5))
                        .filter((country) =>
                          country.location_canonical_name
                            .toLowerCase()
                            .includes(searchQuery)
                        )
                        .map(({ location_country_code, location_canonical_name }) => (
                          <FormControlLabel
                            key={location_country_code}
                            control={
                              <Checkbox
                                size="small"
                                sx={{
                                  paddingBlock: "2px",
                                  color: "#BAB7B7",
                                  "&.Mui-checked": { color: "#61D5A9" },
                                  "& .MuiSvgIcon-root": { height: "14px", width: "14px" },
                                }}
                                checked={selectedCountries.includes(location_country_code)}
                                onChange={() => handleCountryChange(location_country_code)}
                              />
                            }
                            label={location_canonical_name}
                          />
                        ))}
                    </FormGroup>
                  </Box>
                </Grid>
              );
            }
          )}
        </Grid>
      </Box>

      {!searchQuery && (
        <Box display="flex" justifyContent="left" paddingBlock={1}>
          <Button
            onClick={toggleExpandAllContinents}
            className="loadMoreButton"
            startIcon={
              isExpanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )
            }
          >
            {isExpanded ? "Show Less" : "Load More"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TargetLocations;
